import { useState } from "react";
import Bugsnag from "@bugsnag/js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PrimaryButton } from "@/components/ui/PrimaryButton";
import { APP_CONFIG } from "@/config/app.config";
import { userService } from "@/services/userService";
import { useAuth } from "@/hooks/useAuth";

interface PolicyLinkProps {
  id: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled: boolean;
  href: string;
  linkLabel: string;
}

const PolicyCheckbox = ({ id, checked, onCheckedChange, disabled, href, linkLabel }: PolicyLinkProps) => (
  <div className="flex flex-row items-start gap-2">
    <Checkbox
      id={id}
      checked={checked}
      onCheckedChange={(value) => onCheckedChange(value === true)}
      disabled={disabled}
      className="data-[state=checked]:bg-bodhi-teal-dark"
    />
    <Label
      htmlFor={id}
      className="flex flex-wrap items-center gap-1 text-xs font-normal text-muted-foreground hover:cursor-pointer"
    >
      <span>I have read and agree to the</span>
      <a href={href} target="_blank" rel="noopener noreferrer" className="font-semibold hover:underline">
        {linkLabel}
      </a>
    </Label>
  </div>
);

/**
 * Terms and Privacy acceptance for sessions that did not pass through the login
 * screen's code step — i.e. OAuth sign-ins (AE-206). Unlike the login step,
 * this screen IS the enforcement point, so a failed write keeps the user here
 * instead of letting them continue with a warning.
 */
export const PolicyAcceptanceScreen = ({
  userId,
  email,
  onAccepted,
}: {
  userId: string;
  email: string;
  onAccepted: () => void;
}) => {
  const { signOut } = useAuth();
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleContinue = async () => {
    if (!privacyAccepted || !termsAccepted) {
      setError("You must accept both the Privacy Policy and the Terms and Conditions to continue.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await userService.saveUserAcceptance({
        userId,
        email,
        termsEffectiveDate: APP_CONFIG.CURRENT_TERMS_EFFECTIVE_DATE,
        privacyEffectiveDate: APP_CONFIG.CURRENT_PRIVACY_EFFECTIVE_DATE,
      });
      onAccepted();
    } catch (saveError) {
      Bugsnag.notify(saveError instanceof Error ? saveError : new Error(String(saveError)), (event) => {
        event.severity = "warning";
        event.context = "policyAcceptance.save";
      });
      setError("We could not save your acceptance. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mx-auto h-fit w-full max-w-md">
      <CardHeader>
        <CardTitle className="mb-1">Before you continue</CardTitle>
        <CardDescription>
          Please review and accept these to use Hey Bodhi with{" "}
          <span className="font-medium text-foreground">{email}</span>
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4 p-3 sm:p-6">
        <PolicyCheckbox
          id="acceptance-privacy"
          checked={privacyAccepted}
          onCheckedChange={setPrivacyAccepted}
          disabled={saving}
          href={APP_CONFIG.PRIVACY_POLICY_URL}
          linkLabel="Privacy Policy"
        />
        <PolicyCheckbox
          id="acceptance-terms"
          checked={termsAccepted}
          onCheckedChange={setTermsAccepted}
          disabled={saving}
          href={APP_CONFIG.TERMS_AND_CONDITIONS_URL}
          linkLabel="Terms and Conditions"
        />

        {error && <p className="text-xs text-red-500">{error}</p>}

        <PrimaryButton className="w-full" nativeType="button" onClick={() => void handleContinue()} disabled={saving}>
          {saving ? "Saving..." : "Continue"}
        </PrimaryButton>
        <Button type="button" variant="secondary" className="w-full" onClick={() => void signOut()}>
          Sign out
        </Button>
      </CardContent>
    </Card>
  );
};
